import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users, Send, Eye, CheckCircle, TrendingUp, Calendar, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { campaignAPI } from '../../../api';
import { useFormat } from '../../../i18n/format.js';

function CampaignDetailModal({ campaign, onClose }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['campaign-stats', campaign.id],
    queryFn: () => campaignAPI.getStats(campaign.id),
    select: (data) => data?.data || {}
  });

  const stats = statsData || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('campaigns.detail.statsTitle', { name: campaign.name })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">
                  {stats.total_targeted || 0}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t('campaigns.detail.overview.targeted')}</p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Send className="w-5 h-5 text-green-600" />
                <span className="text-2xl font-bold text-green-600">
                  {stats.total_sent || 0}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t('campaigns.detail.overview.sent')}</p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-5 h-5 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">
                  {stats.total_read || 0}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t('campaigns.detail.overview.read')}</p>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-orange-600" />
                <span className="text-2xl font-bold text-orange-600">
                  {stats.total_responses || 0}
                </span>
              </div>
              <p className="text-sm text-gray-600">{t('campaigns.detail.overview.responses')}</p>
            </div>
          </div>

          {/* Rates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t('campaigns.detail.rates.readRate')}</span>
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-gray-900">{stats.read_rate || '0'}</span>
                <span className="ml-1 text-lg text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('campaigns.detail.rates.readOutOf', { read: stats.total_read || 0, sent: stats.total_sent || 0 })}
              </p>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t('campaigns.detail.rates.responseRate')}</span>
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold text-gray-900">{stats.response_rate || '0'}</span>
                <span className="ml-1 text-lg text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('campaigns.detail.rates.responsesOutOf', { responses: stats.total_responses || 0, sent: stats.total_sent || 0 })}
              </p>
            </div>
          </div>

          {/* By Status */}
          {stats.by_status && Object.keys(stats.by_status).length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">{t('campaigns.detail.byStatus.title')}</h3>
              <div className="space-y-2">
                {Object.entries(stats.by_status).map(([status, count]) => {
                  const statusColors = {
                    sent: 'bg-gray-200',
                    read: 'bg-blue-200',
                    responded: 'bg-green-200',
                    expired: 'bg-red-200',
                    dismissed: 'bg-gray-300'
                  };
                  const config = {
                    label: statusColors[status] ? t(`campaigns.responseStatus.${status}`) : status,
                    color: statusColors[status] || 'bg-gray-200'
                  };

                  return (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{config.label}</span>
                      <div className="flex items-center space-x-2">
                        <div className={`h-2 rounded-full ${config.color}`} style={{ width: `${(count / (stats.total_sent || 1)) * 100}px` }} />
                        <span className="text-sm font-medium text-gray-900 w-12 text-right">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Campaign Info */}
          <div className="border-t border-gray-200 pt-6 space-y-3">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('campaigns.detail.info.title')}</h3>

            {campaign.created_at && (
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{t('campaigns.card.createdOn', { date: fmt.dateTime(campaign.created_at) })}</span>
              </div>
            )}

            {campaign.last_sent_at && (
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-2" />
                <span>{t('campaigns.card.lastSentOn', { date: fmt.dateTime(campaign.last_sent_at) })}</span>
              </div>
            )}

            {campaign.scheduled_at && campaign.status !== 'completed' && (
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{t('campaigns.detail.info.nextSendOn', { date: fmt.dateTime(campaign.scheduled_at) })}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CampaignDetailModal;
